import React, { KeyboardEvent, ChangeEvent, useEffect } from 'react';
import { useDebounce } from 'use-debounce';
import {
  KlevuFetch,
  KlevuKMCSettings,
  KlevuResultEvent,
  KlevuTypeOfRecord,
  search,
  suggestions,
  sendSearchEvent,
  KlevuConfig,
  KlevuSearchOptions,
} from '@klevu/core';

import { SearchField } from './Components/SearchField.tsx';
import { SearchSource } from './Enum/SearchSource.ts';
import { KlevuUrlMap } from './Interfaces/interfaces.ts';
import { TrackSearchOpen, TrackSearchRedirect } from '../SiteHeaderTracking.js';
import { NavigationTrackingType } from '../NavigationTrackingType.js';
import { Activities } from './Components/Activities.tsx';
import { CategorySection } from './Components/CategorySection.tsx';
import { useAppState } from '../AppStateContext.tsx';
import { PopularSection } from './Components/PopularSection.tsx';
import { SuggestionsSection } from './Components/SuggestionsSection.tsx';
import { ProductSection } from './Components/ProductsSection.tsx';

let clickManager: KlevuResultEvent['searchClickEvent'];

export const QuickSearch = () => {
  const {
    showSearch,
    setShowSearch,
    setProducts,
    searchTerm,
    setSearchTerm,
    setSuggestions,
    setCategories,
    kmcSettings,
    setKmcSettings,
  } = useAppState();

  const [debounceSearchPhrase] = useDebounce(searchTerm, 300);
  const translations = window._THULEDATA._siteHeader.Translations;

  useEffect(() => {
    KlevuConfig.init({
      url: window._KlevuConfig.url,
      apiKey: window._KlevuConfig.apiKey,
    });

    fetchKMCSettings();

    let params = new URLSearchParams(window.location.search);
    if (params.has('search')) {
      setSearchTerm(params.get('search') || '');
    }
  }, []);

  useEffect(() => {
    searchKlevu(debounceSearchPhrase);
  }, [debounceSearchPhrase]);

  useEffect(() => {
    document.body?.classList.toggle('overflow-hidden', showSearch);
    document.documentElement?.classList.toggle('overflow-hidden', showSearch);

    if (showSearch) {
      TrackSearchOpen();
      document.body.addEventListener('touchmove', handleTouchMove, { passive: false });
    }

    return () => {
      document.body.removeEventListener('touchmove', handleTouchMove);
    };
  }, [showSearch]);

  useEffect(() => {
    if (searchTerm.length === 0) {
      setProducts([]);
      setSuggestions([]);
      setCategories([]);
    }
  }, [searchTerm]);

  const handleTouchMove = (e: any) => {
    if (document.activeElement?.id === 'quick-search-input') {
      e.preventDefault();

      if (e.target.closest('#quickSearchResults') !== null) {
        console.log('scrolling within search results');
        (document.activeElement as HTMLInputElement)?.blur();
      }
    }
  };

  const searchKlevu = async (query: string): Promise<void> => {
    if (query.length < 3) return;

    const ids = {
      search: `products${Date.now()}`,
      category: `categories${Date.now()}`,
      suggestions: `suggestions${Date.now()}`,
    };

    const searchOptions: Partial<KlevuSearchOptions> = {
      id: ids.search,
      limit: 4,
      typeOfRecords: [KlevuTypeOfRecord.Product],
      groupBy: 'id',
      visibilityGroupID: window._THULEDATA.SitecoreLanguage,
      priceFieldSuffix: `${window._THULEDATA.currencyCode}-${window._THULEDATA.SitecoreLanguage}`,
    };

    const categoryOptions = {
      id: ids.category,
      limit: 4,
      typeOfRecords: [KlevuTypeOfRecord.Category],
    };

    const result = await KlevuFetch(
      search(query, searchOptions, sendSearchEvent()),
      search(query, categoryOptions),
      suggestions(query, { id: ids.suggestions })
    );

    const searchResult = result.queriesById(ids.search);
    clickManager = searchResult.searchClickEvent?.bind(searchResult);

    setProducts(searchResult?.records ?? []);
    setSuggestions(result.suggestionsById(ids.suggestions)?.suggestions.map((i) => i.suggest) ?? []);
    setCategories(result.queriesById(ids.category)?.records ?? []);
  };

  const fetchKMCSettings = async (): Promise<void> => {
    setKmcSettings(await KlevuKMCSettings(true));
  };

  const handleSearch = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key !== 'Enter' && e.key !== 'Escape') return;

    if (e.key === 'Escape') {
      setShowSearch(false);
      return;
    }

    doSearch();
  };

  const doSearch = (): void => {
    const searchUrl = kmcSettings?.maps?.klevu_keywordUrlMap?.find((m: KlevuUrlMap) =>
      m.keywords.includes(searchTerm.toLowerCase())
    )?.url;

    if (searchUrl) {
      TrackSearchRedirect(searchTerm);
      window.location.href = searchUrl;
    } else {
      window.location.href = `${window._THULEDATA._siteHeader.SearchPageLink.Url}?search=${searchTerm}&source=${SearchSource.SearchTermManually}`;
    }
  };

  return (
    <>
      <div className="quick-search">
        <SearchField
          id="quick-search-input"
          label={translations?.Search}
          placeholder={translations?.Search}
          value={searchTerm}
          showButton={showSearch}
          onKeyUp={handleSearch}
          onFocus={() => setShowSearch(true)}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            if (e.target.value.length > 0) setShowSearch(true);
            setSearchTerm(e.target.value);
          }}
          onClick={doSearch}
        />
      </div>
      <div className={`quick-search-overlay pt-9 px-6 ${showSearch ? '' : 'd-none'}`}>
        <div className="quick-search-results-container">
          <div className="quick-search-results" id="quickSearchResults">
            <div className="quick-search-links mb-8 mb-lg-9">
              <PopularSection />
              <SuggestionsSection />
              <CategorySection />
            </div>
            <ProductSection clickManager={clickManager} />
            <Activities />
          </div>
        </div>
      </div>
    </>
  );
};
